import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { LineReveal, FadeUp, Stagger, staggerItem, EASE } from "../components/Reveal";
import Marquee from "../components/Marquee";
import WorldMap from "../components/WorldMap";
import { CAPABILITIES } from "../data/capabilities";
import { BOARD } from "../data/leadership";

const STRENGTHS = [
  { n: "01", title: "Global Engineering Expertise", body: "Multidisciplinary teams delivering complex programs across three continents and four decades of practice." },
  { n: "02", title: "Scientific Innovation", body: "Independent laboratories and applied research pipelines that translate discovery into deployable systems." },
  { n: "03", title: "Responsible Resource Development", body: "Ethical extraction, transparent supply chains, and long-term stewardship of the environments we operate in." },
  { n: "04", title: "Advanced Research", body: "Investment in fundamental science across reactor physics, orbital systems, materials, robotics, and AI." },
  { n: "05", title: "International Partnerships", body: "Trusted collaborations with governments, agencies, universities, and industrial partners worldwide." },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative min-h-[100svh] pt-28 md:pt-36 pb-16 overflow-hidden bg-white" data-testid="home-hero">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          <motion.div style={{ y: textY }} className="md:col-span-8">
            <FadeUp delay={0.1}>
              <div className="flex items-center gap-3 mb-8">
                <span className="w-6 h-px bg-[var(--gold)]" />
                <p className="eyebrow-gold">Est. 1968 · Houston · Multinational</p>
              </div>
            </FadeUp>

            <LineReveal
              as="h1"
              lines={["Engineering", "Tomorrow's Critical", "Technologies."]}
              className="h-display text-[clamp(3rem,9vw,9.5rem)] text-[var(--navy)]"
              stagger={0.09}
            />

            <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8">
              <div className="md:col-span-7">
                <FadeUp delay={0.7}>
                  <p className="text-[var(--charcoal)] text-lg md:text-xl leading-relaxed font-light">
                    Aurelius Dynamics develops advanced solutions in nuclear engineering, space research, and strategic mineral resources through scientific innovation, engineering excellence, and global collaboration.
                  </p>
                </FadeUp>
                <FadeUp delay={0.85} className="mt-10 flex flex-wrap gap-4">
                  <Link
                    to="/capabilities"
                    data-testid="home-hero-explore-btn"
                    className="group inline-flex items-center gap-3 bg-[var(--navy)] text-white px-8 py-4 text-sm uppercase tracking-[0.18em] hover:bg-[var(--navy-deep)] transition-colors duration-300"
                  >
                    Explore Our Capabilities
                    <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </Link>
                  <Link
                    to="/contact"
                    data-testid="home-hero-contact-btn"
                    className="group inline-flex items-center gap-3 border border-[var(--navy)] text-[var(--navy)] px-8 py-4 text-sm uppercase tracking-[0.18em] hover:bg-[var(--navy)] hover:text-white transition-colors duration-300"
                  >
                    Contact Us
                    <ArrowRight size={16} className="transition-transform duration-500 group-hover:translate-x-1" />
                  </Link>
                </FadeUp>
              </div>
              <FadeUp delay={0.95} className="md:col-span-5 md:pl-8 md:border-l md:border-[var(--line)]">
                <p className="eyebrow mb-4">Fields of Practice</p>
                <ul className="space-y-3 text-[var(--navy)] font-serif text-xl">
                  <li className="flex items-baseline gap-4"><span className="font-mono text-xs text-[var(--gold-soft)]">N-01</span> Nuclear Technology</li>
                  <li className="flex items-baseline gap-4"><span className="font-mono text-xs text-[var(--gold-soft)]">S-02</span> Space Research</li>
                  <li className="flex items-baseline gap-4"><span className="font-mono text-xs text-[var(--gold-soft)]">M-03</span> Strategic Minerals</li>
                </ul>
              </FadeUp>
            </div>
          </motion.div>

          <div className="md:col-span-4 relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
              animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
              transition={{ duration: 1.4, ease: EASE, delay: 0.3 }}
              className="relative h-[420px] md:h-[560px] w-full overflow-hidden bg-[var(--navy)]"
            >
              <motion.img
                style={{ y: imgY, scale: imgScale }}
                src="https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?auto=format&fit=crop&w=1200&q=80"
                alt="Satellite over Earth"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)]/60 via-transparent to-transparent" />
              <div className="absolute left-4 bottom-4 right-4 text-white">
                <p className="eyebrow-gold text-[10px]">Program No. 07 / Orbital Systems</p>
                <p className="font-serif text-2xl mt-2 leading-tight">Aurelius-VII Earth-observation platform, 2024.</p>
              </div>
              <div className="absolute top-4 left-4 flex items-center gap-2 text-white/80">
                <span className="w-1.5 h-1.5 bg-[var(--gold)]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em]">Live · 29.7604° N, 95.3698° W</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom meta strip */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 mt-16 md:mt-24">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 border-t border-[var(--line)] pt-8">
          {[
            { k: "1968", v: "Founded" },
            { k: "3", v: "Continents · NA / EU / Asia" },
            { k: "42+", v: "Sovereign & Institutional Partners" },
            { k: "5,800", v: "Engineers & Scientists" },
          ].map((s, i) => (
            <FadeUp key={i} delay={0.1 + i * 0.08}>
              <p className="font-serif text-4xl md:text-5xl text-[var(--navy)]">{s.k}</p>
              <p className="mt-2 text-[var(--steel)] text-xs uppercase tracking-[0.18em]">{s.v}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function Overview() {
  return (
    <section className="py-24 md:py-40" data-testid="home-overview">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-3">
          <FadeUp><p className="eyebrow-gold">§ 01 — The Company</p></FadeUp>
        </div>
        <div className="md:col-span-8 md:col-start-4">
          <LineReveal
            as="h2"
            lines={["A multinational engineering", "corporation, quietly", "building critical systems", "for the coming century."]}
            className="h-section text-[clamp(2rem,5vw,4.5rem)] text-[var(--navy)]"
          />
          <FadeUp delay={0.5}>
            <p className="mt-10 text-[var(--charcoal)] text-lg leading-relaxed max-w-2xl">
              Headquartered in Houston, Texas and operating across North America, Europe, and Asia, Aurelius Dynamics provides advanced engineering, scientific research, technology development, consulting, and strategic resource management for governments, research institutions, defense organisations, and commercial partners worldwide.
            </p>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function BusinessHighlights() {
  const highlights = CAPABILITIES.slice(0, 3);
  return (
    <section className="py-24 md:py-32 bg-[var(--paper)]" data-testid="home-highlights">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-16">
          <div className="md:col-span-4"><p className="eyebrow-gold">§ 02 — Divisions</p></div>
          <div className="md:col-span-8">
            <FadeUp>
              <h2 className="h-section text-[clamp(2rem,5vw,4rem)] text-[var(--navy)]">
                Three disciplines. <br />
                <span className="italic text-[var(--gold-soft)]">One engineering culture.</span>
              </h2>
            </FadeUp>
          </div>
        </div>

        <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--line)]">
          {highlights.map((h) => (
            <motion.article
              key={h.id}
              variants={staggerItem}
              className="group bg-white p-8 md:p-10 flex flex-col justify-between min-h-[520px] relative overflow-hidden"
              data-testid={`highlight-card-${h.id}`}
            >
              <div className="relative overflow-hidden aspect-[4/5] mb-8">
                <img
                  src={h.image}
                  alt={h.title}
                  className="w-full h-full object-cover grayscale-[15%] transition-all duration-[1200ms] ease-out group-hover:scale-[1.08] group-hover:grayscale-0"
                />
                <div className="absolute top-3 left-3 bg-white/95 px-3 py-1 font-mono text-[10px] tracking-[0.2em] text-[var(--navy)]">
                  {h.number}
                </div>
              </div>
              <div>
                <h3 className="font-serif text-3xl md:text-4xl text-[var(--navy)] leading-tight">
                  {h.title}
                </h3>
                <p className="mt-4 text-[var(--steel)] leading-relaxed">{h.lead}</p>
                <Link
                  to="/capabilities"
                  className="mt-8 inline-flex items-center gap-2 text-[var(--navy)] text-xs uppercase tracking-[0.2em] group/link"
                  data-testid={`highlight-link-${h.id}`}
                >
                  <span className="link-underline">Read the discipline</span>
                  <ArrowUpRight size={14} className="transition-transform duration-500 group-hover/link:translate-x-1 group-hover/link:-translate-y-1" />
                </Link>
              </div>
            </motion.article>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="py-24 md:py-40" data-testid="home-manifesto">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <p className="eyebrow-gold">§ 03 — Manifesto</p>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 h-section text-[clamp(2.2rem,4.5vw,3.6rem)] text-[var(--navy)]">
              Why Aurelius Dynamics.
            </h2>
            <p className="mt-6 text-[var(--steel)] leading-relaxed max-w-sm">
              Five commitments that shape how our engineers, scientists, and operators approach every program we undertake.
            </p>
          </FadeUp>
        </div>

        <div className="md:col-span-8">
          <Stagger className="divide-y divide-[var(--line)] border-y border-[var(--line)]">
            {STRENGTHS.map((s) => (
              <motion.div
                key={s.n}
                variants={staggerItem}
                className="grid grid-cols-12 gap-4 py-8 md:py-10 group hover:bg-[var(--paper)] transition-colors duration-300"
              >
                <div className="col-span-2 md:col-span-1 font-mono text-[var(--gold-soft)] text-sm pt-1">{s.n}</div>
                <div className="col-span-10 md:col-span-5">
                  <h3 className="font-serif text-2xl md:text-3xl text-[var(--navy)]">{s.title}</h3>
                </div>
                <div className="col-span-12 md:col-span-6 text-[var(--steel)] leading-relaxed">{s.body}</div>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </div>
    </section>
  );
}

function MarqueeStrip() {
  return (
    <Marquee items={["Nuclear Technology", "Space Research", "Strategic Minerals", "Advanced Engineering", "Scientific Innovation", "Global Collaboration"]} />
  );
}

function GlobalPresence() {
  return (
    <section className="py-24 md:py-32 bg-[var(--paper)]" data-testid="home-global">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-4">
            <p className="eyebrow-gold">§ 04 — Global Presence</p>
          </div>
          <div className="md:col-span-8">
            <FadeUp>
              <h2 className="h-section text-[clamp(2rem,5vw,4rem)] text-[var(--navy)]">
                Operations across three continents.
              </h2>
              <p className="mt-6 text-[var(--steel)] leading-relaxed max-w-xl">
                Regional headquarters and research campuses in North America, Europe, and Asia — connected by shared engineering standards and a single scientific culture.
              </p>
            </FadeUp>
          </div>
        </div>
        <FadeUp delay={0.2}>
          <WorldMap />
        </FadeUp>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--line)] border border-[var(--line)]">
          {[
            { r: "North America", pillar: "Nuclear · Aerospace · Government Programs", people: "1,850+" },
            { r: "Europe", pillar: "Fusion · Space Systems · Advanced Materials", people: "2,100+" },
            { r: "Asia", pillar: "Strategic Minerals · Engineering · Innovation", people: "1,850+" },
          ].map((r, i) => (
            <FadeUp key={i} delay={0.1 * i} className="bg-white p-8">
              <p className="eyebrow">{`Region 0${i + 1}`}</p>
              <h3 className="mt-3 font-serif text-3xl text-[var(--navy)]">{r.r}</h3>
              <p className="mt-4 text-[var(--steel)]">{r.pillar}</p>
              <p className="mt-6 font-mono text-xs text-[var(--gold-soft)]">{r.people} PROFESSIONALS</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function LeadershipPreview() {
  const preview = BOARD.slice(0, 6);
  return (
    <section className="py-24 md:py-32" data-testid="home-leadership">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-4"><p className="eyebrow-gold">§ 05 — Leadership</p></div>
          <div className="md:col-span-8">
            <FadeUp>
              <h2 className="h-section text-[clamp(2rem,5vw,4rem)] text-[var(--navy)]">
                Guided by scientists <br />
                <span className="italic">and engineers.</span>
              </h2>
            </FadeUp>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--line)] border border-[var(--line)]">
          {preview.map((p, i) => (
            <FadeUp key={p.name} delay={i * 0.05} className="bg-white p-8 flex items-baseline justify-between gap-6 group hover:bg-[var(--paper)] transition-colors duration-300">
              <div>
                <p className="font-serif text-2xl text-[var(--navy)] leading-tight">{p.name}</p>
                <p className="mt-2 text-sm text-[var(--steel)]">{p.role}</p>
              </div>
              <span className="font-mono text-xs text-[var(--gold-soft)]">{String(i + 1).padStart(2, "0")}</span>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.2} className="mt-10">
          <Link
            to="/about"
            data-testid="home-leadership-cta"
            className="inline-flex items-center gap-3 text-[var(--navy)] text-xs uppercase tracking-[0.2em] group"
          >
            <span className="link-underline">Meet the full leadership</span>
            <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
}

function Sustainability() {
  return (
    <section className="py-24 md:py-32 bg-[var(--navy)] text-white relative overflow-hidden" data-testid="home-sustainability">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 relative z-10 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <p className="eyebrow-gold">§ 06 — Sustainability</p>
        </div>
        <div className="md:col-span-8">
          <LineReveal
            as="h2"
            lines={["Responsible engineering", "is engineering", "worth doing."]}
            className="h-section text-[clamp(2rem,5vw,4rem)] text-white"
          />
          <FadeUp delay={0.5}>
            <p className="mt-10 max-w-2xl text-white/80 text-lg leading-relaxed">
              Every program we accept is measured against a single standard: does it serve the long-term interests of the environments, communities, and institutions we operate within? From reactor safety to ethical mineral sourcing, stewardship is inseparable from craft.
            </p>
          </FadeUp>
          <FadeUp delay={0.7}>
            <Link
              to="/capabilities"
              className="mt-10 inline-flex items-center gap-3 border border-[var(--gold)] text-[var(--gold)] px-8 py-4 text-sm uppercase tracking-[0.18em] hover:bg-[var(--gold)] hover:text-[var(--navy)] transition-colors duration-300"
              data-testid="home-sustainability-cta"
            >
              Sustainability Practice
              <ArrowUpRight size={16} />
            </Link>
          </FadeUp>
        </div>
      </div>
      <div className="absolute right-0 top-0 h-full w-1/3 pointer-events-none opacity-25">
        <img
          src="https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?auto=format&fit=crop&w=1200&q=80"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
    </section>
  );
}

function ClosingCTA() {
  return (
    <section className="py-24 md:py-40" data-testid="home-cta">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 border-t border-b border-[var(--line)] py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
        <div className="md:col-span-8">
          <p className="eyebrow-gold mb-6">Partnerships · Research · Engineering Solutions</p>
          <LineReveal
            as="h2"
            lines={["Let's build the next", "critical technology,", "together."]}
            className="h-display text-[clamp(2.5rem,7vw,7rem)] text-[var(--navy)]"
          />
        </div>
        <FadeUp delay={0.4} className="md:col-span-4 md:text-right">
          <Link
            to="/contact"
            data-testid="home-closing-cta"
            className="inline-flex items-center gap-3 bg-[var(--navy)] text-white px-10 py-5 text-sm uppercase tracking-[0.2em] hover:bg-[var(--gold-soft)] transition-colors duration-500"
          >
            Begin a Conversation
            <ArrowUpRight size={16} />
          </Link>
          <p className="mt-6 text-sm text-[var(--steel)]">contact@aureliusdynamics.com<br />+1 (713) 555-0186</p>
        </FadeUp>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div>
      <Hero />
      <MarqueeStrip />
      <Overview />
      <BusinessHighlights />
      <Manifesto />
      <GlobalPresence />
      <LeadershipPreview />
      <Sustainability />
      <ClosingCTA />
    </div>
  );
}
